import { useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";

const ORIGINAL_TITLE = "RodBez Whatsapp";

/**
 * Realtime conversations hook — backed by Server-Sent Events from the backend.
 *
 * Initial load: fetches /api/messages and /api/contacts once.
 * Live updates: opens an EventSource to /api/stream?token=... and merges:
 *   • "message"        — single message to append to its phone bucket
 *   • "contact"        — metadata refresh for one contact
 *   • "message_status" — update delivery tick (sent → delivered → read)
 *   • "reaction"       — attach/remove a reaction on an existing message
 *   • "notification"   — handled elsewhere (useUserNotifications)
 *
 * Falls back to a 10s polling cycle if SSE fails to connect (networks with
 * aggressive proxies). No Firebase.
 */
export function useConversations({ soundOn = true } = {}) {
  const [contactsMeta, setContactsMeta] = useState({});
  const [messagesByPhone, setMessagesByPhone] = useState({});
  const [typingByPhone, setTypingByPhone] = useState({}); // phone -> {user_id, user_name, ts}
  const [viewersByPhone, setViewersByPhone] = useState({}); // phone -> [{user_id, user_name, ts}]
  const [claimsByPhone, setClaimsByPhone] = useState({}); // phone -> {user_id, user_name, expires_at, ...}
  const [source, setSource] = useState("connecting");
  const [lastUpdate, setLastUpdate] = useState(null);
  const seenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);
  const swRegRef = useRef(null);
  const unreadCountRef = useRef(0);
  const esRef = useRef(null);
  const pollRef = useRef(null);

  const playDing = () => {};

  const updateTitleBadge = () => {
    if (unreadCountRef.current > 0 && document.hidden) {
      document.title = `(${unreadCountRef.current}) ${ORIGINAL_TITLE}`;
    } else {
      document.title = ORIGINAL_TITLE;
    }
  };

  const showSystemNotification = (msg) => {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    const title = msg.name || "+" + msg.from;
    const body = (msg.text || "New message").slice(0, 160);
    const tag = `rodbez-${msg.from}`;
    if (swRegRef.current && swRegRef.current.active) {
      try {
        swRegRef.current.active.postMessage({ type: "NOTIFY", title, body, tag, url: "/" });
        return;
      } catch (_) {}
    }
    try {
      const n = new Notification(title, { body, tag, silent: true });
      n.onclick = () => {
        try { window.focus(); } catch (_) {}
        n.close();
      };
    } catch (_) {}
  };

  const notifyNew = (msg) => {
    if (!soundOn) return;
    if (!initializedRef.current) return;
    // Normal new-message ding disabled per user request — Complaint alarm &
    // Chat-assigned TTS are handled in useUserNotifications and remain ON.
    if (document.hidden) {
      unreadCountRef.current += 1;
      updateTitleBadge();
      try { if ("vibrate" in navigator) navigator.vibrate([180, 80, 180]); } catch (_) {}
      showSystemNotification(msg);
    }
  };

  // Service worker + notification permission + visibility handlers
  // (PWA SW also handles legacy notification logic — per user rule
  // 2026-02-15 we consolidated notify-sw + pwa-sw to avoid Chrome
  // service-worker scope conflicts that were suppressing the Install
  // prompt).
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/pwa-sw.js")
        .then((reg) => { swRegRef.current = reg; })
        .catch(() => {});
    }
    if ("Notification" in window && Notification.permission === "default") {
      setTimeout(() => {
        try { Notification.requestPermission().catch(() => {}); } catch (_) {}
      }, 1500);
    }
    const onVis = () => {
      if (!document.hidden) {
        unreadCountRef.current = 0;
        updateTitleBadge();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    const onSwMsg = (e) => {
      if (e.data && e.data.type === "FOCUS_CHAT") {
        try { window.focus(); } catch (_) {}
      }
    };
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", onSwMsg);
    }
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", onSwMsg);
      }
      document.title = ORIGINAL_TITLE;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------- Event mergers -------
  const mergeMessage = (m) => {
    if (!m || !m.id || !m.from) return;
    setMessagesByPhone((prev) => {
      const bucket = prev[m.from] ? [...prev[m.from]] : [];
      const idx = bucket.findIndex((x) => x.id === m.id);
      if (idx >= 0) {
        bucket[idx] = { ...bucket[idx], ...m };
        return { ...prev, [m.from]: bucket };
      }
      // RACE-CONDITION GUARD: if this is an outgoing message being echoed
      // back from the server (real wamid), look for an unresolved
      // `optim_*` placeholder with the same text — it's the same message
      // we just optimistically rendered. Replace the placeholder's id
      // in place rather than appending a 2nd bubble.
      if (
        m.direction === "outgoing" &&
        typeof m.id === "string" &&
        !m.id.startsWith("optim_")
      ) {
        const optimIdx = bucket.findIndex(
          (x) =>
            typeof x.id === "string" &&
            x.id.startsWith("optim_") &&
            x.direction === "outgoing" &&
            (x.text || "") === (m.text || "") &&
            x.status !== "failed"
        );
        if (optimIdx >= 0) {
          bucket[optimIdx] = {
            ...bucket[optimIdx],
            ...m,
            // Preserve the rendered timestamp from the placeholder so the
            // bubble doesn't visually jump on echo.
            timestamp: bucket[optimIdx].timestamp || m.timestamp,
          };
          return { ...prev, [m.from]: bucket };
        }
      }
      bucket.push(m);
      bucket.sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""));
      return { ...prev, [m.from]: bucket };
    });
    if (m.direction === "inbound" && !seenIdsRef.current.has(m.id)) {
      seenIdsRef.current.add(m.id);
      notifyNew(m);
    } else {
      seenIdsRef.current.add(m.id);
    }
  };

  // Lazy-load message history for a single phone. Initial call returns the
  // last N messages (default 10); subsequent calls with `before=<oldest_ts>`
  // fetch the next older page. ChatWindow uses this to support
  // scroll-to-load-older. Required because the global /messages endpoint
  // only returns the latest 500 across ALL contacts — once the inbox grows
  // beyond that, an older active chat would silently lose its history.
  const loadStateRef = useRef({}); // phone -> { hasMore, oldestTs, loading, initialized }
  const PAGE_SIZE = 10;
  const INITIAL_PAGE_SIZE = 30;

  const loadConversationHistory = async (phone, opts = {}) => {
    if (!phone) return { loaded: 0, hasMore: false };
    const { older = false, forceRefresh = false } = opts;
    // First page loads 30 messages so agents can scroll a meaningful chunk
    // of history immediately. Older pages stay at 10 for fast incremental
    // scroll-up loading. Callers may override via opts.limit.
    const limit = opts.limit ?? (older ? PAGE_SIZE : INITIAL_PAGE_SIZE);
    const state = loadStateRef.current[phone] || {
      hasMore: true,
      oldestTs: null,
      loading: false,
      initialized: false,
    };
    if (state.loading) return { loaded: 0, hasMore: state.hasMore };
    // Initial load only runs once per phone (event-stream keeps it fresh
    // after). `forceRefresh: true` bypasses this gate — used by the
    // SSE-safety-net in Dashboard.jsx when the sidebar's `contact` event
    // arrived with a newer last_timestamp than our local bucket (i.e. the
    // `message` SSE event was lost to a proxy somewhere).
    if (!older && state.initialized && !forceRefresh) {
      return { loaded: 0, hasMore: state.hasMore };
    }
    if (older && !state.hasMore) return { loaded: 0, hasMore: false };
    state.loading = true;
    loadStateRef.current[phone] = state;
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (older && state.oldestTs) params.set("before", state.oldestTs);
      const r = await api.get(
        `/conversations/${phone}/messages?${params.toString()}`,
      );
      const items = r.data?.messages || [];
      const hasMore = !!r.data?.has_more;
      const newOldest = r.data?.oldest_ts || state.oldestTs;
      // Merge into messagesByPhone, deduped by id. Older pages prepend, the
      // initial page just seeds the bucket.
      if (items.length > 0) {
        setMessagesByPhone((prev) => {
          const existing = prev[phone] || [];
          const byId = new Map(existing.map((m) => [m.id, m]));
          for (const m of items) {
            if (!m?.id) continue;
            if (!byId.has(m.id)) {
              byId.set(m.id, m);
              seenIdsRef.current.add(m.id);
            }
          }
          const merged = Array.from(byId.values());
          merged.sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""));
          return { ...prev, [phone]: merged };
        });
      }
      state.hasMore = hasMore;
      state.oldestTs = newOldest || state.oldestTs;
      state.initialized = true;
      state.loading = false;
      loadStateRef.current[phone] = state;
      return { loaded: items.length, hasMore };
    } catch (_) {
      state.loading = false;
      loadStateRef.current[phone] = state;
      return { loaded: 0, hasMore: state.hasMore };
    }
  };

  const mergeContact = (c) => {
    if (!c || !c.phone) return;
    // Shallow-merge so partial SSE events (e.g. an event that only carries
    // `assigned_to`) don't blow away other fields like the just-computed
    // `department`, `rodbez_last_ride_status`, `unread`, etc. The contact
    // map should always represent the union of everything we know.
    setContactsMeta((prev) => ({
      ...prev,
      [c.phone]: { ...(prev[c.phone] || {}), ...c },
    }));
  };

  // Bulk merge contact rows (e.g. server-side search results) into the
  // contactsMeta map. Adds new entries and keeps any locally-known field
  // that the server didn't return (like a richer `complaint` snapshot).
  const mergeContactsList = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    setContactsMeta((prev) => {
      const next = { ...prev };
      for (const c of rows) {
        if (!c?.phone) continue;
        next[c.phone] = { ...(next[c.phone] || {}), ...c };
      }
      return next;
    });
  };

  const mergeStatus = (s) => {
    if (!s || !s.id || !s.phone) return;
    setMessagesByPhone((prev) => {
      const bucket = prev[s.phone];
      if (!bucket) return prev;
      const idx = bucket.findIndex((x) => x.id === s.id);
      if (idx < 0) return prev;
      const next = [...bucket];
      next[idx] = {
        ...next[idx],
        status: s.status,
        status_ts: s.status_ts,
        // Per user rule 2026-02-14 — persist Meta's failure reason on
        // the message so the red ✕ tick can render the tooltip ("why
        // didn't this go?"). Cleared when status is anything else.
        ...(s.status === "failed" && s.failure
          ? { failure: s.failure }
          : s.status !== "failed"
          ? { failure: null }
          : {}),
      };
      return { ...prev, [s.phone]: next };
    });
  };

  const mergeReaction = (r) => {
    if (!r || !r.target_id || !r.phone) return;
    setMessagesByPhone((prev) => {
      const bucket = prev[r.phone];
      if (!bucket) return prev;
      const idx = bucket.findIndex((x) => x.id === r.target_id);
      if (idx < 0) return prev;
      const next = [...bucket];
      const msg = { ...next[idx] };
      const reactions = { ...(msg.reactions || {}) };
      if (r.emoji) reactions[r.from] = r.emoji;
      else delete reactions[r.from];
      msg.reactions = reactions;
      next[idx] = msg;
      return { ...prev, [r.phone]: next };
    });
  };

  const mergeTyping = (t) => {
    if (!t || !t.phone) return;
    setTypingByPhone((prev) => {
      const next = { ...prev };
      if (t.typing) {
        next[t.phone] = {
          user_id: t.user_id,
          user_name: t.user_name,
          ts: Date.now(),
        };
      } else if (next[t.phone] && next[t.phone].user_id === t.user_id) {
        delete next[t.phone];
      }
      return next;
    });
  };

  const mergeViewing = (v) => {
    if (!v || !v.phone) return;
    setViewersByPhone((prev) => {
      const next = { ...prev };
      const viewers = Array.isArray(v.viewers) ? v.viewers : [];
      if (viewers.length === 0) delete next[v.phone];
      else next[v.phone] = viewers;
      return next;
    });
  };

  const mergeClaim = (c) => {
    if (!c || !c.phone) return;
    setClaimsByPhone((prev) => {
      const next = { ...prev };
      if (c.claim) next[c.phone] = c.claim;
      else delete next[c.phone];
      return next;
    });
  };

  // Sweep expired claims client-side (belt & suspenders — server broadcasts
  // release, but claim TTL is 45s and the sweep catches stragglers).
  useEffect(() => {
    const sweep = setInterval(() => {
      setClaimsByPhone((prev) => {
        const now = Date.now();
        const next = {};
        let changed = false;
        for (const [phone, c] of Object.entries(prev)) {
          const exp = c.expires_at ? Date.parse(c.expires_at) : 0;
          if (exp && exp > now) next[phone] = c;
          else changed = true;
        }
        return changed ? next : prev;
      });
    }, 5000);
    return () => clearInterval(sweep);
  }, []);

  // Sweep stale typing entries (safety net if `typing:false` event is dropped).
  useEffect(() => {
    const sweep = setInterval(() => {
      setTypingByPhone((prev) => {
        const now = Date.now();
        const next = {};
        let changed = false;
        for (const [k, v] of Object.entries(prev)) {
          if (now - v.ts < 5000) next[k] = v;
          else changed = true;
        }
        return changed ? next : prev;
      });
    }, 2000);
    return () => clearInterval(sweep);
  }, []);

  // ------- Initial load + SSE subscription -------
  useEffect(() => {
    let cancelled = false;

    const initialLoad = async () => {
      try {
        const [msgsRes, ctsRes, vRes, clRes] = await Promise.all([
          api.get("/messages"),
          api.get("/contacts?limit=500&hours=24").catch(() => ({ data: { contacts: [] } })),
          api.get("/conversations/viewing/snapshot").catch(() => ({ data: { viewing: {} } })),
          api.get("/conversations/claims/snapshot").catch(() => ({ data: { claims: {} } })),
        ]);
        if (cancelled) return;
        const byPhone = {};
        (msgsRes.data?.messages || []).forEach((m) => {
          if (!byPhone[m.from]) byPhone[m.from] = [];
          byPhone[m.from].push(m);
          seenIdsRef.current.add(m.id);
        });
        Object.values(byPhone).forEach((arr) =>
          arr.sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""))
        );
        // MERGE (don't replace) so per-phone histories already loaded by
        // `loadConversationHistory` aren't wiped on poll-mode refreshes.
        // The global /messages endpoint is capped at 500 latest across all
        // chats; replacing would shrink an already-paginated bucket back
        // to 1-2 messages every time the SSE polling fallback ticks.
        setMessagesByPhone((prev) => {
          const next = { ...prev };
          for (const [phone, fresh] of Object.entries(byPhone)) {
            const existing = next[phone] || [];
            const byId = new Map(existing.map((m) => [m.id, m]));
            for (const m of fresh) {
              if (!m?.id) continue;
              if (!byId.has(m.id)) byId.set(m.id, m);
            }
            const merged = Array.from(byId.values());
            merged.sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""));
            next[phone] = merged;
          }
          return next;
        });
        const ctsMap = {};
        (ctsRes.data?.contacts || []).forEach((c) => {
          if (c.phone) ctsMap[c.phone] = c;
        });
        setContactsMeta(ctsMap);
        setViewersByPhone(vRes.data?.viewing || {});
        setClaimsByPhone(clRes.data?.claims || {});
        setLastUpdate(new Date().toISOString());
        setTimeout(() => {
          initializedRef.current = true;
        }, 500);
      } catch (_) {}
    };

    const openStream = () => {
      const token = localStorage.getItem("wh_token");
      if (!token) return;
      try { esRef.current?.close(); } catch (_) {}
      const url = `${API_BASE}/stream?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      esRef.current = es;
      es.onopen = () => {
        setSource("sse");
        setLastUpdate(new Date().toISOString());
        // SSE is back — kill any polling fallback we'd set up earlier so
        // we don't wastefully re-fetch the global /messages slice on top
        // of live SSE deltas.
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      };
      es.onmessage = (evt) => {
        try {
          const { type, data } = JSON.parse(evt.data);
          setLastUpdate(new Date().toISOString());
          switch (type) {
            case "message":        mergeMessage(data); break;
            case "contact":        mergeContact(data); break;
            case "message_status": mergeStatus(data); break;
            case "reaction":       mergeReaction(data); break;
            case "typing":         mergeTyping(data); break;
            case "viewing":        mergeViewing(data); break;
            case "claim":          mergeClaim(data); break;
            case "hello":
            case "ping":
            case "presence":
            case "notification":   /* handled elsewhere */ break;
            default: break;
          }
        } catch (_) {}
      };
      es.onerror = () => {
        setSource("reconnecting");
        try { es.close(); } catch (_) {}
        // EventSource reconnects automatically in browsers; we also fall
        // back to a gentle 10s poll as a safety net.
        if (!pollRef.current) {
          pollRef.current = setInterval(() => {
            initialLoad();
          }, 10000);
        }
        setTimeout(() => {
          if (!cancelled) openStream();
        }, 3000);
      };
    };

    initialLoad().then(() => {
      if (!cancelled) openStream();
    });

    return () => {
      cancelled = true;
      try { esRef.current?.close(); } catch (_) {}
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build the contacts list from the UNION of contactsMeta (full server-side
  // contact list, up to 2000 from /api/contacts) AND messagesByPhone (any
  // phone that has loaded msgs in this session). Earlier we built it ONLY
  // from messagesByPhone — but /api/messages is capped at 500 globally, so
  // any conversation older than the last 500 msgs would silently disappear
  // from the inbox list (even though the contact existed in DB and would
  // have shown up via /api/contacts). This union ensures search + filters
  // see every contact the server knows about.
  const allPhones = new Set([
    ...Object.keys(contactsMeta || {}),
    ...Object.keys(messagesByPhone || {}),
  ]);
  // Memoize the heavy contacts-list build so re-renders triggered by
  // unrelated state changes (typing indicators, viewer pings, SLA tick)
  // don't pay the O(N log N) sort cost on 3k+ contacts. Recomputes ONLY
  // when contactsMeta / messagesByPhone change — i.e. when there's
  // actual inbox data delta. Without this the sidebar feels laggy on
  // production-sized inboxes.
  const contacts = useMemo(() => Array.from(allPhones)
    .map((phone) => {
      const msgs = messagesByPhone[phone] || [];
      const last = msgs[msgs.length - 1] || {};
      const meta = contactsMeta[phone] || {};
      return {
        phone,
        name: meta.name || last.name || phone,
        last_message: meta.last_message || last.text || "",
        last_timestamp: meta.last_timestamp || last.timestamp || "",
        last_direction: last.direction || meta.last_direction || null,
        status: meta.status || "new",
        assigned_to: meta.assigned_to || null,
        tags: meta.tags || [],
        unread: meta.unread || 0,
        complaint: meta.complaint || null,
        channel: meta.channel || null,
        rodbez_customer_id: meta.rodbez_customer_id || null,
        rodbez_customer_name: meta.rodbez_customer_name || null,
        rodbez_name_verified: !!meta.rodbez_name_verified,
        // Ride-status cache + department chip (Sales / Operations / New).
        // These flow from the bot's persist + agent peek + verify
        // endpoints. If we don't surface them here the sidebar chip stays
        // stuck on "New" forever even after the data lands in contactsMeta.
        rodbez_last_ride_status: meta.rodbez_last_ride_status ?? null,
        rodbez_last_ride_id: meta.rodbez_last_ride_id ?? null,
        rodbez_last_ride_status_updated_at:
          meta.rodbez_last_ride_status_updated_at ?? null,
        department: meta.department || null,
        // Bot mid-flow flag (rodbez_sessions OR bot_sessions has an
        // active step). Surfaced by /api/contacts so the sidebar can
        // show the inline "🤖 bot handling" icon next to the customer
        // name. Without this passthrough the meta exists in contactsMeta
        // but never reaches the rendered contact card → icon never
        // shows even when backend says bot_active=true (per user
        // 2026-02 production bug report).
        bot_active: !!meta.bot_active,
        // Call-request flag — set by `_rodbez_handover` when customer
        // taps "Talk to an agent" / "Talk to Support" / "Direct Call
        // Support" / "Yes, help me" on the missed-call template. Cleared
        // on ticket close. Drives the blinking red phone icon in the
        // sidebar tile AND the Call filter count.
        call_request: !!meta.call_request,
        call_request_at: meta.call_request_at || null,
        call_request_reason: meta.call_request_reason || null,
      };
    })
    .sort((a, b) => (b.last_timestamp || "").localeCompare(a.last_timestamp || "")),
    [contactsMeta, messagesByPhone]
  );

  // ------- Optimistic UI helpers (instant outgoing message render) ------
  // Append a placeholder outgoing message to the bucket immediately, so
  // the agent sees their reply on screen the moment they hit Send — no
  // waiting for /api/reply round-trip + SSE echo.
  const addOptimistic = (m) => {
    if (!m || !m.id || !m.from) return;
    setMessagesByPhone((prev) => {
      const bucket = prev[m.from] ? [...prev[m.from]] : [];
      // Avoid double-insert if caller retries
      if (bucket.some((x) => x.id === m.id)) return prev;
      bucket.push(m);
      bucket.sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""));
      return { ...prev, [m.from]: bucket };
    });
  };
  // Replace an optimistic placeholder by tempId — used to swap the temp
  // id for the real wamid once the server responds, OR to mark the
  // bubble as `failed` if the API call errors out.
  //
  // RACE-CONDITION GUARD: the server's SSE echo of the saved outgoing
  // message can arrive BEFORE the api.post() promise resolves. In that
  // case `mergeMessage` already appended a bubble with the real wamid,
  // so renaming our optim_* placeholder to the same wamid would create
  // a duplicate bubble. Detect this and just delete the placeholder.
  const replaceOptimistic = (phone, tempId, patch) => {
    if (!phone || !tempId) return;
    setMessagesByPhone((prev) => {
      const bucket = prev[phone] ? [...prev[phone]] : [];
      const idx = bucket.findIndex((x) => x.id === tempId);
      if (idx < 0) return prev;
      const newId = patch?.id;
      if (newId && newId !== tempId) {
        // SSE may have already inserted the real message — check.
        const realIdx = bucket.findIndex((x) => x.id === newId);
        if (realIdx >= 0) {
          // Real bubble already present → drop the optim placeholder
          // and merge any patch fields onto the real one.
          bucket[realIdx] = { ...bucket[realIdx], ...patch };
          bucket.splice(idx, 1);
          return { ...prev, [phone]: bucket };
        }
      }
      bucket[idx] = { ...bucket[idx], ...patch };
      return { ...prev, [phone]: bucket };
    });
  };
  // Remove an optimistic placeholder entirely (e.g. user dismisses a
  // failed bubble).
  const removeOptimistic = (phone, tempId) => {
    if (!phone || !tempId) return;
    setMessagesByPhone((prev) => {
      const bucket = (prev[phone] || []).filter((x) => x.id !== tempId);
      return { ...prev, [phone]: bucket };
    });
  };

  return { contacts, messagesByPhone, typingByPhone, viewersByPhone, claimsByPhone, source, lastUpdate, loadConversationHistory, mergeContactsList, addOptimistic, replaceOptimistic, removeOptimistic };
}
